import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  Grid,
  Box,
  Typography,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  Archive as ArchiveIcon,
} from "@mui/icons-material";

import { grey } from "@mui/material/colors";

import { headers, fetchChatGPTResponse } from "../../utils/utils";
import Comments from "../../ui/Comments";
import AuditDisplay from "../../ui/AuditDisplay";
import message from "../../../locales/message.json";
import WorkTimer from "../Ticket/TimeSheet/WorkTimer";
import WorkTimerTable from "../Ticket/TimeSheet/WorkTimerTable";
import SubjectField from "./sections/SubjectField";
import PrioritySelect from "./sections/PrioritySelect";
import MessageBox from "./sections/MessageBox";
import TagSelector from "./sections/TagSelector";
import MemberSelector from "./sections/MemberSelector";
import DateSelectors from "./sections/DateSelectors";
import AttachmentSection from "./sections/AttachmentSection";
import DynamicFields from "./sections/DynamicFields";
import TaskSection from "./sections/TaskSection";




const NewTicket = ({
  openNewTicket,
  setOpenNewTicket,
  ticketStatus,
  setTicketStatus,
  handleNewTicket,
  firstTicketLabelName,
  formData,
  setFormData,
  tagList,
  memberList,
  loadAttachments,
  attachments,
  displayAlertMessage,
  updateTicketStatus,
}) => {
  const [ticketData, setTicketData] = useState({
    subject: "",
    message: "",
    priority: "",
    memberIds: [],
    tags: [],
    id: null,
    board: localStorage.getItem("board"),
    dueDate: null,
    targetDate: null,
    status: "",
    ticketItems: [],
    dynamicFields: {},
  });

  const [aiSuggestions, setAiSuggestions] = useState({ subject: "", message: "" });
  const [aiSuggestionsVisible, setAiSuggestionsVisible] = useState({ message: false, priority: false });
  const [dynamicFields, setDynamicFields] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allowRequest, setAllowRequest] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const workTimerRef = useRef();
  const storedBoardId = localStorage.getItem("board");

  // Merge formData into ticketData
  useEffect(() => {
    setTicketData((prev) => ({ ...prev, ...formData }));
  }, [formData]);

  // Set default status
  useEffect(() => {
    if (firstTicketLabelName) {
      setTicketData((prev) => ({ ...prev, status: firstTicketLabelName }));
    }
  }, [firstTicketLabelName]);

  // Load dynamic fields
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_TICKET_SERVER}/ticket/dynamicFieldsLoad/${storedBoardId}`,
          { method: "GET", headers: headers() }
        );
        if (res.ok) setDynamicFields(await res.json());
      } catch (err) {
        console.error("Error fetching dynamic fields:", err);
      }
    };
    if (storedBoardId) fetchFields();
  }, [storedBoardId]);

  const mergedFields = dynamicFields.map((f) => ({ ...f, value: formData.dynamicFields?.[f.label] || "" }));

  // Helpers
  const isFormValid = () =>
    Object.entries(ticketData).some(([key, val]) => {
      if (["board", "status"].includes(key)) return false;
      if (Array.isArray(val)) return val.length > 0;
      return val !== null && val !== "";
    });

  const raiseTicket = async (data) => {
    if (!isFormValid()) return;
    console.log("Raising ticket with data:", data );
    try {
      let res;
      if (data.id) {
        res = await fetch(`${process.env.REACT_APP_TICKET_SERVER}/ticket/update/${data.id}`, {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify(data),
        });
      } else if (allowRequest) {
        setAllowRequest(false);
        res = await fetch(`${process.env.REACT_APP_TICKET_SERVER}/ticket/save`, {
          method: "POST",
          headers: headers(),
          body: JSON.stringify(data),
        });
      }
      const resData = await res.json();
      if (resData) {
        updateTicketStatus(resData);
        setTicketData(resData);
        setTimeout(() => setAllowRequest(true), 1000);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const handleArchive = () => setShowConfirmation(true);

  const handleConfirmArchive = async (confirm) => {
    if (confirm) {
      const res = await fetch(`${process.env.REACT_APP_TICKET_SERVER}/ticket/delete/${ticketData.id}`, {
        method: "DELETE",
        headers: headers(),
      });
      if (res.ok) {
        setTicketStatus((prev) =>
          prev.map((s) => ({ ...s, ticketList: s.ticketList.filter((t) => t.id !== ticketData.id) }))
        );
        resetForm();
        setOpenNewTicket(false);
        displayAlertMessage("Archived Successfully");
      }
    }
    setShowConfirmation(false);
  };

  const duplicateTicket = () => {
    const copy = { ...ticketData, id: null, code: null, ticketItems: ticketData.ticketItems.map((i) => ({ ...i, id: null })) };
    setTicketData(copy);
    handleNewTicket();
    raiseTicket(copy);
  };

  const resetForm = () => {
    if (workTimerRef.current) workTimerRef.current.stopWorkSession();
    setTicketData({
      subject: "",
      message: "",
      board: localStorage.getItem("board"),
      priority: "",
      memberIds: [],
      id: null,
      dueDate: null,
      startDateTime: null,
      endDateTime: null,
      targetDate: null,
      dynamicFields: {},
      status: firstTicketLabelName,
    });
    setAiSuggestions({ subject: "", message: "" });
    setAiSuggestionsVisible({ message: false, priority: false });
    handleNewTicket();
  };

  const handleBlur = async () => {
    setIsEditing(false);
    try {
      const prompt = `Correct the following subject: "${ticketData.subject}". Provide only the corrected version.`;
      const corrected = await fetchChatGPTResponse(prompt);
      setAiSuggestions((prev) => ({ ...prev, subject: corrected }));
    } catch (err) {
      // AI suggestion is optional — never let it block saving the actual subject edit.
      console.error("AI subject suggestion unavailable:", err);
    }
    raiseTicket(ticketData);
  };

  return (
    <Dialog open={openNewTicket} onClose={resetForm} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary"><strong>Card</strong></Typography>
          {ticketData?.code && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>#{ticketData.code}</Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={10.5}>
            <SubjectField
              aiSuggestions={aiSuggestions}
              aiSuggestionsVisible={aiSuggestionsVisible}
              ticketData={ticketData}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleChange={(e) => setTicketData({ ...ticketData, [e.target.name]: e.target.value })}
              handleBlur={handleBlur}
            
              handleAIMessageForm={(label) => {
                const updated = { ...ticketData, [label]: aiSuggestions[label] };
                setTicketData(updated);
                raiseTicket(updated);
                setAiSuggestionsVisible((p) => ({ ...p, [label]: false }));
                setAiSuggestions((p) => ({ ...p, [label]: "" }));
              }}
              
            />
          </Grid>

          <Grid item xs={1}>
            <PrioritySelect
              value={ticketData.priority}
              ticketData={ticketData}
              setTicketData={setTicketData}
              raiseTicket={raiseTicket}
            />
          </Grid>

          <Grid item xs={12}>
            <MessageBox ticketData={ticketData} handleChange={(e) => setTicketData({ ...ticketData, [e.target.name]: e.target.value })} clearPreviousSuggestions={() => setTimeout(() => setAiSuggestions((p) => ({ ...p, subject: "" })), 10000)} raiseTicket={raiseTicket} displayAlertMessage={displayAlertMessage} message={message} />
            <TagSelector ticketData={ticketData} setTicketData={setTicketData} raiseTicket={raiseTicket} displayAlertMessage={displayAlertMessage} message={message} tagList={tagList} workTimerRef={workTimerRef} WorkTimer={WorkTimer} />
            <MemberSelector ticketData={ticketData} setTicketData={setTicketData} raiseTicket={raiseTicket} displayAlertMessage={displayAlertMessage} message={message} memberList={memberList} />

            <Divider sx={{ borderBottomWidth: 1, borderColor: grey[300], my: 2 }} />
            <DateSelectors formData={formData} ticketData={ticketData} setFormData={setFormData} setTicketData={setTicketData} raiseTicket={raiseTicket} />
            <Divider sx={{ borderBottomWidth: 1, borderColor: grey[300], my: 2 }} />

            <DynamicFields mergedFields={mergedFields} formData={formData} setFormData={setFormData} raiseTicket={raiseTicket} />
            <AttachmentSection attachments={attachments} ticketData={ticketData} raiseTicket={raiseTicket} loadAttachments={loadAttachments} />
            <TaskSection ticketData={ticketData} setTicketData={setTicketData} raiseTicket={raiseTicket} fetchChatGPTResponse={fetchChatGPTResponse} displayAlertMessage={displayAlertMessage} message={message} memberList={memberList} handleToggleEdit={(i, editing) => setTicketData((prev) => ({ ...prev, ticketItems: prev.ticketItems.map((it, idx) => idx === i ? { ...it, isEditing: editing } : it) }))} />
          </Grid>
        </Grid>

        <Comments ticket={ticketData} displayAlertMessage={displayAlertMessage} />
        <WorkTimerTable ticketId={ticketData.id} memberList={memberList} />
        <AuditDisplay ticket={ticketData} memberList={memberList} tagList={tagList} />

        <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="contained" color="primary" onClick={duplicateTicket}>Copy Card</Button>
            <Button variant="contained" color="error" startIcon={<ArchiveIcon />} onClick={handleArchive}>Archive</Button>
          </Box>
        </DialogActions>

        {showConfirmation && (
          <Box sx={{ mt: 1, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button variant="outlined" color="success" onClick={() => handleConfirmArchive(true)}>Yes</Button>
            <Button variant="outlined" color="error" onClick={() => handleConfirmArchive(false)}>No</Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewTicket;
